import { ReleaseCitySubdivisionSourceValue } from '@outkeep/shared';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  input,
  effect,
  computed,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TuiButton } from '@taiga-ui/core';
import { XAlertService } from '@core/services';
import {
  CounterpartySubdivision,
  ReleaseCityTranslations,
  ReleaseCityTranslationsPayload,
} from '@core/types';
import { FormPickerComponent } from '@core/components';
// Импорт AttachSubdivisionsDirective должен быть полным для исключения циклических зависимостей
import { AttachSubdivisionsDirective } from '@features/requests/request-form/components/request-form-common/components/request-form-common-main/directives/attach-subdivisions.directive';
import { RequestModel } from '@core/models';
import { RequestsApiService } from '@core/services/request';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import type { CityState } from './request-form-release-city.types';

@Component({
  selector: 'x-request-form-release-city',
  imports: [
    ReactiveFormsModule,
    FormPickerComponent,
    AttachSubdivisionsDirective,
    TuiButton,
    TranslatePipe,
  ],
  templateUrl: './request-form-release-city.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormReleaseCityComponent {
  readonly request = input.required<RequestModel>();

  private readonly requestAPIService = inject(RequestsApiService);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly cityState = signal<CityState>('none');
  readonly initialSubdivisionId = signal<number | null>(null);
  readonly contractorId = computed(
    () => this.request().contractorCounterpartyId?.id ?? null,
  );
  readonly releaseCityControl = new FormControl<CounterpartySubdivision | null>(
    null,
  );

  private translates: ReleaseCityTranslationsPayload = {
    [ReleaseCityTranslations.Success]: 'Город выдачи успешно установлен',
    [ReleaseCityTranslations.Error]: 'Ошибка установки города выдачи',
  };

  constructor() {
    effect(() => {
      const releaseCitySubdivisionId = this.request().releaseCitySubdivisionId;

      if (!releaseCitySubdivisionId) {
        this.cityState.set('none');

        return;
      }

      const isManual =
        this.request().releaseCitySubdivisionSource ===
        ReleaseCitySubdivisionSourceValue.Manual;

      this.cityState.set(isManual ? 'manually' : 'auto');
    });

    effect(() => {
      const id = this.request().releaseCitySubdivisionId;

      this.initialSubdivisionId.set(id ?? null);
    });

    this.outkeepTranslateService
      .translate(Object.values(ReleaseCityTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  setSubdivisionIdManually() {
    const selectedCity = this.releaseCityControl.value;

    if (!selectedCity) {
      return;
    }

    return this.requestAPIService
      .setSubdivisionIdManually$(this.request().id, selectedCity.id)
      .pipe(take(1))
      .subscribe({
        next: result => {
          this.request().releaseCitySubdivisionId =
            result.releaseCitySubdivisionId;
          this.request().releaseCitySubdivisionSource =
            result.releaseCitySubdivisionSource;
          this.cityState.set('manually');
          this.initialSubdivisionId.set(
            result.releaseCitySubdivisionId ?? null,
          );
          this.xAlertService.success(
            this.translates[ReleaseCityTranslations.Success],
          );
        },
        error: () => {
          this.xAlertService.error(
            this.translates[ReleaseCityTranslations.Error],
          );
        },
      });
  }
}
